import { Button, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useAdditionalFeatureIsEnabled } from '$components/additional-feature-guard/additional-feature-is-enabled';
import {
  ReportHubBetaAccessDialog,
  ReportHubUpsellDialog,
} from '$features/compliance-upsell/report-hub-upsell-nav-item';
import {
  MonitoringReportDialogs,
  MonitoringReportDialogProps,
  MonitoringReportSuccessDialog,
} from '$features/monitoring/report/monitoring-report-dialogs';
import { useCreateReport } from '$features/monitoring/report/report.api';
import {
  isEssentialsPlan,
  useGetSubscription,
} from '$features/subscription/subscription-api';

export interface CreateReportButtonProps {
  className?: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
  serialNumber: string;
  manufacturer: string;
  initialDateRange?: { from?: Date; to?: Date };
}

export function CreateReportButton({
  className,
  variant = 'secondary',
  serialNumber,
  manufacturer,
  initialDateRange,
}: CreateReportButtonProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isUpsellDialogOpen, setIsUpsellDialogOpen] = useState(false);
  const [isBetaAccessDialogOpen, setIsBetaAccessDialogOpen] = useState(false);
  const createReportMutation = useCreateReport();
  const { data: subscription } = useGetSubscription();
  const hasReportHubAccess = useAdditionalFeatureIsEnabled('REPORT_HUB_ACCESS');

  const handleOpenDialog = () => {
    if (isEssentialsPlan(subscription)) {
      setIsUpsellDialogOpen(true);
      return;
    }

    if (!hasReportHubAccess) {
      setIsBetaAccessDialogOpen(true);
      return;
    }

    setIsDialogOpen(true);
  };

  const handleSave: MonitoringReportDialogProps['onSave'] = async (data) => {
    await createReportMutation.mutateAsync({
      startDate: data.dateRange.from.toISOString(),
      endDate: data.dateRange.to.toISOString(),
      serialNumber,
      manufacturer,
      notes: data.notes,
      deviceName: data.deviceName,
      deviceModel: data.deviceModel,
      reportType: 'Stability',
      selectedTelemetryIds: data.selectedTelemetryIds,
    });

    setIsDialogOpen(false);
    setIsSuccessDialogOpen(true);
  };

  return (
    <>
      <div className="flex flex__ai--center flex__jc--space-between w-full">
        <Link to="/report-hub/stability" className="color-blue-500">
          <span className="flex flex__ai--center gap-xs">
            {t('monitoring.report.viewAllReports')}
            <Icon
              name="movement-right"
              className="bg-blue-500"
              size={IconSizeClasses.XSmall}
            />
          </span>
        </Link>
        <Button
          variant={variant}
          onClick={handleOpenDialog}
          className={className}
          size="small"
        >
          <span className="flex flex__ai--center gap-xs">
            <Icon name="plus" className="bg-blue-500" size={IconSizeClasses.XSmall} />
            {t('monitoring.report.createButton')}
          </span>
        </Button>
      </div>

      <MonitoringReportDialogs
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        deviceId={serialNumber}
        manufacturer={manufacturer}
        initialDateRange={initialDateRange}
        onSave={handleSave}
      />

      <MonitoringReportSuccessDialog
        isOpen={isSuccessDialogOpen}
        onOpenChange={setIsSuccessDialogOpen}
        onViewDetails={() => navigate('/report-hub/stability')}
      />

      <ReportHubUpsellDialog
        isOpen={isUpsellDialogOpen}
        onOpenChange={setIsUpsellDialogOpen}
      />
      <ReportHubBetaAccessDialog
        isOpen={isBetaAccessDialogOpen}
        onOpenChange={setIsBetaAccessDialogOpen}
      />
    </>
  );
}
